/**
 * Risk Assessment Intake Dialog
 *
 * Allows clinicians to complete the HCV risk assessment questionnaire
 * on behalf of a patient during an intake session.
 */

import { useState, useMemo } from 'react'
import { router } from '@inertiajs/react'
import { AlertCircleIcon, CheckCircle2Icon, ChevronRightIcon, ChevronLeftIcon } from 'lucide-react'
import { toast } from 'sonner'
import authService from '@/lib/auth'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Separator } from '@/components/ui/separator'

export interface QuestionnaireResponse {
  id: number
  response_text: string
  point_value: number
  display_order: number
}

export interface QuestionnaireQuestion {
  id: number
  section_name: string
  question_text: string
  question_type: string
  required: boolean
  display_order: number
  responses: QuestionnaireResponse[]
}

interface RiskAssessmentIntakeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  patientId: number
  patientName: string
  questions: QuestionnaireQuestion[]
  onSuccess?: () => void
}

const RISK_COLORS: Record<string, string> = {
  low: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-800',
  moderate: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800',
  high: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-800',
  critical: 'bg-red-200 dark:bg-red-900/50 text-red-900 dark:text-red-200 border-red-300 dark:border-red-700',
}

export function RiskAssessmentIntakeDialog({
  open,
  onOpenChange,
  patientId,
  patientName,
  questions,
  onSuccess,
}: RiskAssessmentIntakeDialogProps) {
  const [responses, setResponses] = useState<Record<number, number>>({})
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [result, setResult] = useState<{ risk_level: string; calculated_risk_score: string; total_score: number } | null>(null)

  // Group questions by section
  const sections = useMemo(() => {
    const grouped: Record<string, QuestionnaireQuestion[]> = {}
    questions.forEach((q) => {
      if (!grouped[q.section_name]) grouped[q.section_name] = []
      grouped[q.section_name].push(q)
    })
    return Object.entries(grouped).map(([name, qs]) => ({ name, questions: qs }))
  }, [questions])

  const currentSection = sections[currentSectionIndex]
  const totalSections = sections.length
  const progressPct = totalSections > 0 ? ((currentSectionIndex + 1) / totalSections) * 100 : 0

  const requiredQuestionsInSection = currentSection?.questions.filter((q) => q.required) ?? []
  const allRequiredAnswered = requiredQuestionsInSection.every((q) => responses[q.id] !== undefined)

  const totalRequired = questions.filter((q) => q.required)
  const totalAnswered = totalRequired.filter((q) => responses[q.id] !== undefined)
  const allDone = totalAnswered.length === totalRequired.length

  const handleSelectResponse = (questionId: number, responseId: number) => {
    setResponses((prev) => ({ ...prev, [questionId]: responseId }))
  }

  const handleNext = () => {
    if (currentSectionIndex < totalSections - 1) {
      setCurrentSectionIndex((i) => i + 1)
    }
  }

  const handleBack = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex((i) => i - 1)
    }
  }

  const handleSubmit = async () => {
    if (!allDone) {
      toast.error('Please answer all required questions before submitting.')
      return
    }

    setIsSubmitting(true)
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      const response = await fetch(`/admin/patients/${patientId}/create_risk_assessment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          ...authService.authHeaders(),
        },
        body: JSON.stringify({ responses }),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setResult(data)
      } else {
        toast.error(data.errors?.join(', ') || 'Failed to save assessment')
      }
    } catch {
      toast.error('An unexpected error occurred. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (result) {
      onSuccess?.()
      router.reload()
    }
    setResponses({})
    setCurrentSectionIndex(0)
    setResult(null)
    onOpenChange(false)
  }

  if (!open) return null

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircleIcon className="h-5 w-5 text-orange-500" />
            HCV Risk Assessment
          </DialogTitle>
          <DialogDescription>
            Completing assessment for <span className="font-medium text-foreground">{patientName}</span>.
            Answer all required questions to calculate the risk score.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          /* ── Success State ── */
          <div className="flex flex-col items-center gap-6 py-6">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
              <CheckCircle2Icon className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <div className="text-center space-y-2">
              <h3 className="text-lg font-semibold">Assessment Complete</h3>
              <p className="text-sm text-muted-foreground">
                Risk score calculated successfully
              </p>
            </div>
            <div className={`rounded-xl border px-8 py-4 text-center ${RISK_COLORS[result.calculated_risk_score] || RISK_COLORS.low}`}>
              <div className="text-3xl font-bold capitalize">{result.risk_level}</div>
              <div className="text-sm mt-1 opacity-80">Total score: {result.total_score}</div>
            </div>
            <p className="text-xs text-muted-foreground text-center max-w-sm">
              The patient's care case has been opened automatically. You can view the full assessment on their profile.
            </p>
          </div>
        ) : (
          /* ── Questionnaire State ── */
          <>
            {/* Progress */}
            <div className="space-y-2 pt-1">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Section {currentSectionIndex + 1} of {totalSections}</span>
                <span>{totalAnswered.length}/{totalRequired.length} required questions answered</span>
              </div>
              <Progress value={progressPct} className="h-1.5" />
            </div>

            {/* Section heading */}
            {currentSection && (
              <div className="font-semibold text-sm text-muted-foreground uppercase tracking-wide pt-1">
                {currentSection.name}
              </div>
            )}

            <Separator />

            {/* Questions */}
            <div className="flex-1 overflow-y-auto space-y-6 pr-1">
              {currentSection?.questions.map((question) => (
                <div key={question.id} className="space-y-3">
                  <div className="flex items-start gap-2">
                    <span className="text-sm font-medium leading-snug">
                      {question.question_text}
                      {question.required && <span className="text-red-500 ml-1">*</span>}
                    </span>
                  </div>
                  <div className="space-y-2 pl-1">
                    {question.responses.map((resp) => {
                      const isSelected = responses[question.id] === resp.id
                      return (
                        <button
                          key={resp.id}
                          type="button"
                          onClick={() => handleSelectResponse(question.id, resp.id)}
                          className={`w-full flex items-center justify-between rounded-lg border px-4 py-2.5 text-sm text-left transition-all ${
                            isSelected
                              ? 'border-primary bg-primary/10 text-primary font-medium'
                              : 'border-border bg-background hover:border-muted-foreground/50 hover:bg-muted/50'
                          }`}
                        >
                          <span>{resp.response_text}</span>
                          {resp.point_value > 0 && (
                            <Badge
                              variant="outline"
                              className={`ml-2 text-xs shrink-0 ${
                                resp.point_value >= 4
                                  ? 'border-red-300 text-red-600 dark:text-red-400'
                                  : resp.point_value >= 2
                                  ? 'border-yellow-300 text-yellow-600 dark:text-yellow-400'
                                  : 'border-green-300 text-green-600 dark:text-green-400'
                              }`}
                            >
                              +{resp.point_value}
                            </Badge>
                          )}
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <DialogFooter className="gap-2 pt-2">
          {result ? (
            <Button onClick={handleClose}>
              View Patient Profile
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              {currentSectionIndex > 0 && (
                <Button variant="outline" onClick={handleBack} disabled={isSubmitting}>
                  <ChevronLeftIcon className="h-4 w-4 mr-1" />
                  Back
                </Button>
              )}
              {currentSectionIndex < totalSections - 1 ? (
                <Button onClick={handleNext} disabled={!allRequiredAnswered}>
                  Next
                  <ChevronRightIcon className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={!allDone || isSubmitting}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                >
                  {isSubmitting ? 'Calculating...' : 'Calculate Risk Score'}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
